import medusaRequest from "./request";

const removeNullish = (obj) =>
	Object.entries(obj).reduce((a, [k, v]) => (v ? ((a[k] = v), a) : a), {});

const buildQueryFromObject = (search, prefix = "") =>
	Object.entries(search)
		.map(([key, value]) =>
			typeof value === "object"
				? buildQueryFromObject(value, key)
				: `${prefix ? `${prefix}[${key}]` : `${key}`}=${value}`
		)
		.join("&");

const buildParamsFromSearchObject = (search) => {
	const clean = removeNullish(search);
	const seenKeys = new Set();
	const params = Object.keys(clean)
		.map((k) => {
			// If value is an object, we need to use bracket notation
			if (typeof search[k] === "object" && !Array.isArray(search[k])) {
				// For each key, put it in brackets to get the key, add the value to search
				const bracketedKeys = Object.keys(search[k]).map((nestedKey) => {
					const bracketedKey = `${k}[${nestedKey}]`;
					search[bracketedKey] = search[k][nestedKey];
					return bracketedKey;
				});

				return bracketedKeys;
			}

			return k;
		})
		.flat()
		.filter((k) => {
			if (seenKeys.has(k) && typeof k === "string" && k.includes("[")) {
				return false;
			}
			seenKeys.add(k);
			return true;
		})
		.map((k) => `${k}=${search[k]}`)
		.filter((s) => !!s)
		.join("&");
	return params;
};

export default {
	configuration: {
		get(configKey) {
			const path = `/admin/c/${configKey}`;
			return medusaRequest("GET", path);
		},
	},
	returnReasons: {
		retrieve(id) {
			const path = `/admin/return-reasons/${id}`;
			return medusaRequest("GET", path);
		},
		list() {
			const path = `/admin/return-reasons`;
			return medusaRequest("GET", path);
		},
		create(payload) {
			const path = `/admin/return-reasons`;
			return medusaRequest("POST", path, payload);
		},
		update(id, payload) {
			const path = `/admin/return-reasons/${id}`;
			return medusaRequest("POST", path, payload);
		},
		delete(id) {
			const path = `/admin/return-reasons/${id}`;
			return medusaRequest("DELETE", path);
		},
	},
	apps: {
		authorize(data) {
			const path = `/admin/apps/authorizations`;
			return medusaRequest("POST", path, data);
		},

		list() {
			const path = `/admin/apps`;
			return medusaRequest("GET", path);
		},
	},
	auth: {
		session() {
			const path = `/admin/auth`;
			return medusaRequest("GET", path);
		},
		authenticate(details) {
			const path = `/admin/auth`;
			return medusaRequest("POST", path, details);
		},
		deauthenticate(details) {
			const path = `/admin/auth`;
			return medusaRequest("DELETE", path);
		},
	},
	notifications: {
		list(search = {}) {
			const params = Object.keys(search)
				.map((k) => `${k}=${search[k]}`)
				.join("&");
			const path = `/admin/notifications${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},
		resend(id, config) {
			const path = `/admin/notifications/${id}/resend`;
			return medusaRequest("POST", path, config);
		},
	},
	notes: {
		listByResource(resourceId) {
			const path = `/admin/notes?resource_id=${resourceId}`;
			return medusaRequest("GET", path);
		},
		async create(resourceId, resourceType, value, followUpAt) {
			const path = `/admin/notes/`;
			return medusaRequest("POST", path, {
				resource_id: resourceId,
				resource_type: resourceType,
				value,
				...(followUpAt && { followUpAt: followUpAt }),
			});
		},
		update(id, value) {
			const path = `admin/notes/${id}`;
			return medusaRequest("POST", path, { value });
		},
		delete(id) {
			const path = `admin/notes/${id}`;
			return medusaRequest("DELETE", path);
		},
	},
	features: {
		isEnabled(featureName) {
			const path = `/admin/feature-flags/${featureName}`;
			return medusaRequest("GET", path);
		},
		list(userId) {
			let path = `/admin/feature-flags`;
			if (userId) {
				path += `?userId=${encodeURIComponent(userId)}`;
			}
			return medusaRequest("GET", path);
		},
		overrideForUser(featureName, userId, enabled) {
			const path = `/admin/feature-flags/${featureName}/override`;
			return medusaRequest("POST", path, { userId, enabled });
		},
	},
	customers: {
		isRegistered(email) {
			const path = `/admin/customer/is-registered?email=${email}`;
			return medusaRequest("GET", path);
		},
		retrieve(customerId) {
			const path = `/admin/customers/${customerId}`;
			return medusaRequest("GET", path);
		},
		list(search = "") {
			const path = `/admin/customers${search}`;
			return medusaRequest("GET", path);
		},
		update(customerId, update) {
			const path = `admin/customers/${customerId}`;
			return medusaRequest("POST", path, update);
		},
		getPatient(customerId, orderId) {
			const path = `/admin/customer/${customerId}/patient${orderId ? `?orderId=${orderId}` : ""}`;
			return medusaRequest("GET", path);
		},
		validatePatient(customerId, orderId) {
			const path = `/admin/customer/${customerId}/validate/${orderId}`;
			return medusaRequest("POST", path);
		},
		updatePatient(customerId, patientData) {
			const res = medusaRequest(
				"POST",
				`/admin/customer/${customerId}/patient`,
				patientData
			);
			return res?.customer || {};
		},
		getPatientEvents(customerId, limit = 20, offset = 0, eventType) {
			let path = `/admin/customer/${customerId}/patient/events?limit=${limit}&offset=${offset}`;
			if (eventType) {
				path += `&eventType=${eventType}`;
			}
			return medusaRequest("GET", path);
		},
		getFile(cartId, fileType, index) {
			const path = `/admin/cart/${cartId}/files/${fileType}/${index}`;
			return medusaRequest("GET", path);
		},
		getFiles(cartId, fileType) {
			const path = `/admin/cart/${cartId}/files/${fileType}`;
			return medusaRequest("GET", path);
		},
		listMessagesForCustomer(customer_id, limit = 10, offset = 0) {
			const path = `/admin/customer/${customer_id}/messages?limit=${limit}&offset=${offset}`;
			return medusaRequest("GET", path);
		},
		listCustomerWithMessages(limit = 10, offset = 0, search = "") {
			const path = `/admin/customer/messages?limit=${limit}&offset=${offset}${search ? `&search=${search}` : ""}`;
			return medusaRequest("GET", path);
		},
		async markMessageViewed(customerID, messageID, orderID) {
			const path = `/admin/customer/${customerID}/order/${orderID}/messages/viewed`;
			return medusaRequest("POST", path, { messageId: messageID });
		},
	},
	files: {
		getFileByExternalId(externalId) {
			const path = `/admin/files/external/${externalId}`;
			return medusaRequest("GET", path);
		},
	},
	store: {
		retrieve() {
			const path = `/admin/store`;
			return medusaRequest("GET", path);
		},

		update(update) {
			const path = `/admin/store`;
			return medusaRequest("POST", path, update);
		},

		addCurrency(code) {
			const path = `/admin/store/currencies/${code}`;
			return medusaRequest("POST", path);
		},

		removeCurrency(code) {
			const path = `/admin/store/currencies/${code}`;
			return medusaRequest("DELETE", path);
		},

		listPaymentProviders() {
			const path = `/admin/store/payment-providers`;
			return medusaRequest("GET", path);
		},

		questions(productId) {
			const path = `store/products/${productId}/questions`;
			return medusaRequest("GET", path);
		},

		answer(cartId, questionId) {
			const path = `store/cart/${cartId}/questions/${questionId}/responses`;
			return medusaRequest("GET", path);
		},

		files(cartId, fileType) {
			const path = `store/cart/${cartId}/files/${fileType}`;
			return medusaRequest("GET", path);
		},
	},
	shippingProfiles: {
		list() {
			const path = `/admin/shipping-profiles`;
			return medusaRequest("GET", path);
		},

		create(data) {
			const path = `/admin/shipping-profiles`;
			return medusaRequest("POST", path, data);
		},

		retrieve(profileId) {
			const path = `/admin/shipping-profiles/${profileId}`;
			return medusaRequest("GET", path);
		},

		update(profileId, update) {
			const path = `/admin/shipping-profiles/${profileId}`;
			return medusaRequest("POST", path, update);
		},
	},

	giftCards: {
		create(giftCard) {
			const path = `/admin/gift-cards`;
			return medusaRequest("POST", path, giftCard);
		},

		retrieve(giftCardId) {
			const path = `/admin/gift-cards/${giftCardId}`;
			return medusaRequest("GET", path);
		},

		list(search = {}) {
			const params = Object.keys(search)
				.map((k) => `${k}=${search[k]}`)
				.join("&");
			const path = `/admin/gift-cards${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},

		update(giftCardId, update) {
			const path = `/admin/gift-cards/${giftCardId}`;
			return medusaRequest("POST", path, update);
		},

		delete(giftCardId) {
			const path = `/admin/gift-cards/${giftCardId}`;
			return medusaRequest("DELETE", path);
		},
	},

	variants: {
		list(search = {}) {
			const params = Object.keys(search)
				.map((k) => `${k}=${search[k]}`)
				.join("&");
			const path = `/admin/variants${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},
	},

	products: {
		create(product) {
			const path = `/admin/products`;
			return medusaRequest("POST", path, product);
		},

		retrieve(productId) {
			const path = `/admin/products/${productId}`;
			return medusaRequest("GET", path);
		},

		update(productId, update) {
			const path = `/admin/products/${productId}`;
			return medusaRequest("POST", path, update);
		},

		delete(productId) {
			const path = `/admin/products/${productId}`;
			return medusaRequest("DELETE", path);
		},

		list(search = {}) {
			const params = Object.keys(search)
				.map((k) => `${k}=${search[k]}`)
				.join("&");
			const path = `/admin/products${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},

		listTypes() {
			const path = `/admin/products/types`;
			return medusaRequest("GET", path);
		},

		listTagsByUsage() {
			const path = `/admin/products/tag-usage`;
			return medusaRequest("GET", path);
		},

		variants: {
			create(productId, variant) {
				const path = `/admin/products/${productId}/variants`;
				return medusaRequest("POST", path, variant);
			},

			retrieve(productId, variantId) {
				const path = `/admin/products/${productId}/variants/${variantId}`;
				return medusaRequest("GET", path);
			},

			update(productId, variantId, update) {
				const path = `/admin/products/${productId}/variants/${variantId}`;
				return medusaRequest("POST", path, update);
			},

			updateV2(variantId, update, orderId) {
				let path = `/admin/v2/variants/${variantId}`;
				if (orderId) {
					path = `/admin/v2//order/${orderId}/variants/${variantId}`;
				}
				return medusaRequest("PUT", path, update);
			},

			delete(productId, variantId) {
				const path = `/admin/products/${productId}/variants/${variantId}`;
				return medusaRequest("DELETE", path);
			},

			list(productId) {
				const path = `/admin/products/${productId}/variants`;
				return medusaRequest("GET", path);
			},
		},

		options: {
			create(productId, option) {
				const path = `/admin/products/${productId}/options`;
				return medusaRequest("POST", path, option);
			},

			delete(productId, optionId) {
				const path = `/admin/products/${productId}/options/${optionId}`;
				return medusaRequest("DELETE", path);
			},

			update(productId, optionId, update) {
				const path = `/admin/products/${productId}/options/${optionId}`;
				return medusaRequest("POST", path, update);
			},
		},
		addTag(productId, tag) {
			return medusaRequest("POST", `/admin/products/${productId}/tags`, {
				tag,
			});
		},
		removeTag(productId, tagId) {
			return medusaRequest(
				"DELETE",
				`/admin/products/${productId}/tags/${tagId}`
			);
		},
	},

	swaps: {
		retrieve(swapId) {
			const path = `/admin/swaps/${swapId}`;
			return medusaRequest("GET", path);
		},

		update(orderId, update) {
			const path = `/admin/orders/${orderId}`;
			return medusaRequest("POST", path, update);
		},

		list(search = {}) {
			const params = Object.keys(search)
				.map((k) => {
					if (search[k] === "" || search[k] === null) {
						return null;
					}
					return `${k}=${search[k]}`;
				})
				.filter((s) => !!s)
				.join("&");
			const path = `/admin/swaps${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},
	},

	returns: {
		list(search = {}) {
			const clean = removeNullish(search);
			const params = Object.keys(clean)
				.map((k) => `${k}=${search[k]}`)
				.filter((s) => !!s)
				.join("&");
			const path = `/admin/returns${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},
	},

	collections: {
		create(payload) {
			const path = `/admin/collections`;
			return medusaRequest("POST", path, payload);
		},

		retrieve(id) {
			const path = `/admin/collections/${id}`;
			return medusaRequest("GET", path);
		},

		list(search = {}) {
			const path = `/admin/collections`;
			return medusaRequest("GET", path);
		},

		addProducts(id, payload) {
			const path = `/admin/collections/${id}/products/batch`;
			return medusaRequest("POST", path, payload);
		},

		removeProducts(id, payload) {
			const path = `/admin/collections/${id}/products/batch`;
			return medusaRequest("DELETE", path, payload);
		},
	},

	orders: {
		create(order) {
			const path = `/admin/orders`;
			return medusaRequest("POST", path, order);
		},

		listRelatedOrders(orderId, limit = 10, offset = 0) {
			const path = `/admin/orders/${orderId}/related?limit=${limit}&offset=${offset}`;
			return medusaRequest("GET", path);
		},

		async setState(orderId, stateMachineType, stateName) {
			const path = `/admin/orders/${orderId}/state/${stateMachineType}`;
			return medusaRequest("POST", path, { stateName });
		},

		async listStates() {
			const path = `/admin/states`;
			return medusaRequest("GET", path);
		},

		async getTrackingInfo(orderId) {
			const path = `/admin/order/${orderId}/tracking-info`;
			return medusaRequest("GET", path);
		},

		async receiveReturn(returnId, payload) {
			const path = `/admin/returns/${returnId}/receive`;

			const received = await medusaRequest("POST", path, payload);

			let orderId;
			if (received.data.return?.order_id) {
				orderId = received.data.return.order_id;
			}

			if (received.data.return?.swap?.id) {
				orderId = received.data.return?.swap?.order_id;
			}

			return this.retrieve(orderId);
		},

		getQuestionsForCart(cartId) {
			const path = `/admin/products/${cartId}/questions`;
			return medusaRequest("GET", path);
		},

		getAnswerForQuestion(cartId, questionId) {
			const path = `/admin/cart/${cartId}/questions/${questionId}/responses`;
			return medusaRequest("GET", path);
		},

		cancelReturn(returnId) {
			const path = `/admin/returns/${returnId}/cancel`;
			return medusaRequest("POST", path);
		},

		retrieve(orderId, search = {}) {
			const params = Object.keys(search)
				.map((k) => {
					if (search[k] === "" || search[k] === null) {
						return null;
					}
					return `${k}=${search[k]}`;
				})
				.filter((s) => !!s)
				.join("&");
			const path = `/admin/orders/${orderId}${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},

		retrieveV2(orderId) {
			const path = `/admin/v2/orders/${orderId}`;
			return medusaRequest("GET", path);
		},
		retrieveLatest() {
			const path = `/admin/orders/latest`;
			return medusaRequest("GET", path);
		},

		update(orderId, update) {
			const path = `/admin/orders/${orderId}`;
			return medusaRequest("POST", path, update);
		},

		list(search = {}) {
			const clean = removeNullish(search);
			const params = Object.keys(clean)
				.map((k) => `${k}=${search[k]}`)
				.filter((s) => !!s)
				.join("&");

			const path = `/admin/orders${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},

		listWithDetails(search = {}) {
			const clean = removeNullish(search);
			const params = Object.keys(clean)
				.map((k) => `${k}=${search[k]}`)
				.filter((s) => !!s)
				.join("&");

			const path = `/admin/orders/details${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},

		complete(orderId) {
			const path = `/admin/orders/${orderId}/complete`;
			return medusaRequest("POST", path, {});
		},

		archive(orderId) {
			const path = `/admin/orders/${orderId}/archive`;
			return medusaRequest("POST", path, {});
		},

		capturePayment(orderId) {
			const path = `/admin/orders/${orderId}/capture`;
			return medusaRequest("POST", path, {});
		},

		captureSubscriptionPayment(orderId) {
			const path = `/admin/orders/${orderId}/capture-subscription-payment`;
			return medusaRequest("POST", path, {});
		},

		capturePaymentCustomAmount(orderId, payload) {
			const path = `/admin/orders/${orderId}/capture-payment-custom-amount`;
			return medusaRequest("POST", path, payload);
		},

		updatePaymentMethod(orderId, payload) {
			const path = `/admin/orders/${orderId}/update-payment-method`;
			return medusaRequest("POST", path, payload);
		},

		update(orderId, payload) {
			const path = `/admin/orders/${orderId}`;
			return medusaRequest("POST", path, payload);
		},

		refundSubscriptionPayment(orderId) {
			const path = `/admin/orders/${orderId}/refund-subscription-payment`;
			return medusaRequest("POST", path, {});
		},

		createShipment(orderId, payload) {
			const path = `/admin/orders/${orderId}/shipment`;
			return medusaRequest("POST", path, payload);
		},

		updateClaim(orderId, claimId, payload) {
			const path = `/admin/orders/${orderId}/claims/${claimId}`;
			return medusaRequest("POST", path, payload);
		},

		createSwap(orderId, payload) {
			const path = `/admin/orders/${orderId}/swaps`;
			return medusaRequest("POST", path, payload);
		},

		cancelSwap(orderId, swapId) {
			const path = `/admin/orders/${orderId}/swaps/${swapId}/cancel`;
			return medusaRequest("POST", path);
		},

		createClaim(orderId, payload) {
			const path = `/admin/orders/${orderId}/claims`;
			return medusaRequest("POST", path, payload);
		},

		cancelClaim(orderId, claimId) {
			const path = `/admin/orders/${orderId}/claims/${claimId}/cancel`;
			return medusaRequest("POST", path);
		},

		fulfillClaim(orderId, claimId, payload) {
			const path = `/admin/orders/${orderId}/claims/${claimId}/fulfillments`;
			return medusaRequest("POST", path, payload);
		},

		cancelClaimFulfillment(orderId, claimId, fulfillmentId) {
			const path = `/admin/orders/${orderId}/claims/${claimId}/fulfillments/${fulfillmentId}/cancel`;
			return medusaRequest("POST", path);
		},

		createClaimShipment(orderId, cId, payload) {
			const path = `/admin/orders/${orderId}/claims/${cId}/shipments`;
			return medusaRequest("POST", path, payload);
		},

		createSwapShipment(orderId, swapId, payload) {
			const path = `/admin/orders/${orderId}/swaps/${swapId}/shipments`;
			return medusaRequest("POST", path, payload);
		},

		fulfillSwap(orderId, swapId, payload) {
			const path = `/admin/orders/${orderId}/swaps/${swapId}/fulfillments`;
			return medusaRequest("POST", path, payload);
		},

		cancelSwapFulfillment(orderId, swapId, fulfillmentId) {
			const path = `/admin/orders/${orderId}/swaps/${swapId}/fulfillments/${fulfillmentId}/cancel`;
			return medusaRequest("POST", path);
		},

		processSwapPayment(orderId, swapId) {
			const path = `/admin/orders/${orderId}/swaps/${swapId}/process-payment`;
			return medusaRequest("POST", path);
		},

		createFulfillment(orderId, payload) {
			const path = `/admin/orders/${orderId}/fulfillment`;
			return medusaRequest("POST", path, payload);
		},

		fulfillOrder(orderId, payload) {
			const path = `/admin/order/${orderId}/fulfill`;
			return medusaRequest("POST", path, payload);
		},

		cancelFulfillment(orderId, fulfillmentId) {
			const path = `/admin/orders/${orderId}/fulfillments/${fulfillmentId}/cancel`;
			return medusaRequest("POST", path);
		},

		refund(orderId, payload) {
			const path = `/admin/orders/${orderId}/refund`;
			return medusaRequest("POST", path, payload);
		},

		requestReturn(orderId, payload) {
			const path = `/admin/orders/${orderId}/return`;
			return medusaRequest("POST", path, payload);
		},

		cancel(orderId) {
			const path = `/admin/orders/${orderId}/cancel`;
			return medusaRequest("POST", path, {});
		},

		getStateMachine(orderId, abortController = new AbortController()) {
			const path = `/admin/order/${orderId}/state`;
			return medusaRequest("GET", path, abortController);
		},

		getMacrosForState(orderId, abortController = new AbortController()) {
			const path = `/admin/order/${orderId}/macros/recommended`;
			return medusaRequest("GET", path, abortController);
		},

		getMacros() {
			const path = `/admin/order/macros`;
			return medusaRequest("GET", path);
		},

		getMacrosWithProviders(search = "") {
			const path = `/admin/order/macros/with-providers${search}`;
			return medusaRequest("GET", path);
		},

		getEnhancedMacros(params = {}) {
			let path = `/admin/order/macros/enhanced`;
			if (!params.sort) {
				params.sort = "modified_desc";
			}
			const query = new URLSearchParams(params).toString();
			if (query) path += `?${query}`;
			return medusaRequest("GET", path);
		},

		getEnhancedMacroById(id) {
			const path = `/admin/order/macros/${id}`;
			return medusaRequest("GET", path);
		},

		updateEnhancedMacro(id, payload) {
			const path = `/admin/order/macros/${id}`;
			return medusaRequest("PUT", path, payload);
		},

		templateMacro(orderId, macroId) {
			const path = `/admin/order/${orderId}/macros/${macroId}/template`;
			return medusaRequest("GET", path);
		},

		fetchCase(orderId) {
			const path = `/admin/order/${orderId}/encounter`;
			return medusaRequest("GET", path);
		},

		async runMacro(orderId, macroId, payload) {
			const path = `/admin/order/${orderId}/macros/${macroId}/run`;
			const { data } = await medusaRequest("POST", path, payload);
			const retvalFromMacroRun = data?.macroRun?.data;
			return retvalFromMacroRun;
		},

		runMacroRaw(orderId, macroId, payload) {
			const path = `/admin/order/${orderId}/macros/${macroId}/run`;
			return medusaRequest("POST", path, payload);
		},

		checkMacro(orderId, macroId) {
			const path = `/admin/order/${orderId}/macros/${macroId}/check`;
			return medusaRequest("POST", path);
		},

		getTimelineEvents(orderId) {
			const path = `/admin/order/${orderId}/timeline-events`;
			return medusaRequest("GET", path);
		},

		getAvailableShippingOptions(orderId, shippingMethodId) {
			const path = `/admin/order/${orderId}/shipping-method/${shippingMethodId}/shipping-options`;
			return medusaRequest("GET", path);
		},

		getAvailableShippingOptionsV2(getFulfillmentOptions) {
			const path = `/admin/order/shipping-options`;
			return medusaRequest("POST", path, getFulfillmentOptions);
		},
		getEarliestPickupDate() {
			const path = `/admin/order/earliest-pickup-date`;
			return medusaRequest("GET", path);
		},

		setShippingOption(
			orderId,
			shippingMethodId,
			shippingOptionId,
			trackingNumber,
			trackingUrl
		) {
			const path = `/admin/order/${orderId}/shipping-method/${shippingMethodId}/shipping-options/${shippingOptionId}`;
			return medusaRequest("POST", path, { trackingNumber, trackingUrl });
		},

		ship(orderId, fulfillmentId) {
			const path = `/admin/order/${orderId}/fulfillment/${fulfillmentId}/ship`;
			return medusaRequest("POST", path);
		},

		getOrderDetails(orderId) {
			const path = `/admin/order/${orderId}/details`;
			return medusaRequest("GET", path);
		},

		updatePayment(orderId, amount) {
			const path = `/admin/order/${orderId}/update-payment`;
			return medusaRequest("PUT", path, { amount });
		},

		getPaymentMethod(orderId) {
			const path = `/admin/order/${orderId}/payment-method`;
			return medusaRequest("GET", path);
		},

		getOrderStats(search, abortController = new AbortController()) {
			const params = buildParamsFromSearchObject(search);
			const path = `/admin/pharmacy/stats${params && `?${params}`}`;
			return medusaRequest("GET", path, {}, abortController);
		},

		listV2(search = {}, abortController = new AbortController()) {
			const params = buildParamsFromSearchObject(search);
			const path = `/admin/pharmacy/orders${params && `?${params}`}`;
			return medusaRequest("GET", path, {}, abortController);
		},
		search(search = {}, abortController = new AbortController()) {
			const params = buildParamsFromSearchObject(search);
			const path = `/admin/pharmacy/search-orders${params && `?${params}`}`;
			return medusaRequest("GET", path, {}, abortController);
		},

		export(payload) {
			const cleanPayload = Object.entries(payload).reduce(
				(acc, [key, value]) => {
					if (key.includes("[")) {
						const [mainKey, nestedKey] = key.replace("]", "").split("[");
						acc[mainKey] = acc[mainKey] || {};
						acc[mainKey][nestedKey] = value;
					} else {
						acc[key] = value;
					}
					return acc;
				},
				{}
			);
			return medusaRequest("POST", `/admin/orders/export`, cleanPayload);
		},

		getCompounds(orderId) {
			const path = `/admin/order/${orderId}/compounds`;
			return medusaRequest("GET", path);
		},

		async uploadFile(cartId, file, fileType = "ID") {
			const formData = new FormData();
			formData.append("file", file);

			const res = await medusaRequest(
				"POST",
				`/admin/cart/${cartId}/files/${fileType}`,
				formData,
				{
					"Content-Type": "multipart/form-data",
				}
			);

			return res?.data?.file;
		},

		async uploadFileWithIndex(cartId, file, fileType = "ID", index = 0) {
			const formData = new FormData();
			formData.append("file", file);

			const res = await medusaRequest(
				"POST",
				`/admin/cart/${cartId}/files/${fileType}/${index}`,
				formData,
				{
					"Content-Type": "multipart/form-data",
				}
			);

			return res?.file;
		},

		async updateFile(fileId, file, fileType = "ID", suppress = false) {
			const formData = new FormData();
			formData.append("file", file);

			const res = await medusaRequest(
				"POST",
				`/admin/files/${fileId}?suppress=${suppress}`,
				formData,
				{
					"Content-Type": "multipart/form-data",
				}
			);

			return res?.data?.file;
		},

		async swapFiles(fileId1, fileId2) {
			const res = await medusaRequest("POST", "/admin/files/swap", {
				fileId1,
				fileId2,
			});

			return res?.data;
		},

		async assignOrderToPharmacy(orderId, pharmacyId) {
			const path = `/admin/order/${orderId}/assign-pharmacy/${pharmacyId}`;
			return medusaRequest("POST", path);
		},

		async unassignOrderFromPharmacy(orderId) {
			const path = `/admin/order/${orderId}/assign-pharmacy`;
			return medusaRequest("DELETE", path);
		},

		async getPrescription(orderId) {
			const path = `/admin/orders/${orderId}/prescription`;
			return medusaRequest("GET", path);
		},

		async completePrescription(orderId) {
			const path = `/admin/orders/${orderId}/prescription/complete`;
			return medusaRequest("POST", path);
		},

		async getSubscriptionFulfillments(orderId) {
			const path = `/admin/orders/${orderId}/subscription-fulfillments`;
			return medusaRequest("GET", path);
		},

		async updateSubscriptionFulfillment(subscriptionFulfillmentId, updateData) {
			const path = `/admin/subscription-fulfillments/${subscriptionFulfillmentId}`;
			return medusaRequest("PUT", path, updateData);
		},

		async updateSubscriptionFulfillmentItem(
			subscriptionFulfillmentItemId,
			updateData
		) {
			const path = `/admin/subscription-fulfillment-items/${subscriptionFulfillmentItemId}/update`;
			return medusaRequest("PUT", path, updateData);
		},

		async updateSubscriptionItem(orderId, variantId, updateData) {
			// Parse integer values in updateData
			const parsedUpdateData = Object.keys(updateData).reduce((acc, key) => {
				const value = updateData[key];
				// Parse numeric strings to integers, keep other values as-is
				acc[key] =
					typeof value === "string" && !isNaN(value) && !isNaN(parseInt(value))
						? parseInt(value)
						: value;
				return acc;
			}, {});

			const path = `/admin/subscriptions/${orderId}/items/${variantId}`;
			return medusaRequest("PUT", path, parsedUpdateData);
		},

		async listSubscriptionItems(subscriptionId) {
			const path = `/admin/subscriptions/${subscriptionId}/items`;
			return medusaRequest("GET", path);
		},

		async updateSubscriptionState(orderId, data) {
			const path = `/admin/orders/${orderId}/subscription-state`;
			return medusaRequest("PUT", path, data);
		},

		async assignOrderToClinician(orderId, clinicianId) {
			const path = `/admin/order/${orderId}/assign-clinician/${clinicianId}`;
			return medusaRequest("POST", path);
		},

		async unassignOrderToClinician(orderId) {
			const path = `/admin/order/${orderId}/assign-clinician`;
			return medusaRequest("DELETE", path);
		},
		async assignToNewCustomer(orderId, payload) {
			const path = `/admin/order/${orderId}/assign-to-new-customer`;
			return medusaRequest("POST", path, payload);
		},
		async getOrderIdByDisplayId(orderDisplayId) {
			const path = `/admin/order/${orderDisplayId}/orderId`;
			return medusaRequest("GET", path);
		},
		async getOrderByOffsetFromDisplayId(orderDisplayId, offset, search = {}) {
			const params = buildParamsFromSearchObject(search);
			const path = `/admin/pharmacy/order/${orderDisplayId}/offset/${offset}${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},
		async listMessages(orderId) {
			const path = `/admin/order/${orderId}/messages`;
			return medusaRequest("GET", path);
		},
		async markUpdateUnread(orderID, timelineProviderID, timelineEventID) {
			const path = `/admin/order/${orderID}/update/unread`;
			return medusaRequest("POST", path, {
				timelineProviderID,
				timelineEventID,
			});
		},
		async markMessageUnread(orderID, messageID) {
			const path = `/admin/order/${orderID}/message/unread`;
			return medusaRequest("POST", path, { messageID });
		},

		async archiveOrder(orderId) {
			const path = `/admin/orders/${orderId}/archive`;
			return medusaRequest("POST", path);
		},

		async restoreOrder(orderId) {
			const path = `/admin/orders/${orderId}/restore`;
			return medusaRequest("POST", path);
		},

		async tagOrder(orderId, value) {
			const path = `/admin/order/${orderId}/tag`;
			return medusaRequest("POST", path, { value });
		},

		async untagOrder(orderId, tagId) {
			const path = `/admin/order/${orderId}/tag/${tagId}`;
			return medusaRequest("DELETE", path);
		},

		async listTags(orderId) {
			const path = `/admin/order/${orderId}/tag`;
			return medusaRequest("GET", path);
		},

		async listAvailableTags() {
			const path = `/admin/tags`;
			return medusaRequest("GET", path);
		},


		async sendPrescriptionManually(orderId, prescriptionData) {
			const path = `/admin/orders/${orderId}/send-rx`;
			return medusaRequest("POST", path, prescriptionData);
		},

		async removeFulfillmentItem(subscriptionFulfillmentItemId) {
			const path = `/admin/subscription-fulfillment-items/${subscriptionFulfillmentItemId}`;
			return medusaRequest("DELETE", path);
		},

		async updateOrderEditConfiguration(orderEditId, configuration) {
			const path = `/admin/orders/order-edits/${orderEditId}/configuration`;
			return medusaRequest("PUT", path, { configuration });
		},

		async getOrderEditConfiguration(orderEditId) {
			const path = `/admin/orders/order-edits/${orderEditId}/configuration`;
			return medusaRequest("GET", path).then(({ data }) => data);
		},

		async upsertItemToSubscription(
			subscriptionId,
			variantId,
			totalQuantity,
			initialQuantity,
			totalDays
		) {
			const path = `/admin/subscriptions/${subscriptionId}/upsert-item`;
			return medusaRequest("POST", path, {
				variantId,
				totalQuantity,
				initialQuantity,
				totalDays,
			});
		},

		async removeItemFromSubscription(subscriptionId, variantId) {
			const path = `/admin/subscriptions/${subscriptionId}/items/${variantId}`;
			return medusaRequest("DELETE", path);
		},

		getMacroVersions(macroId) {
			const path = `/admin/order/macros/${macroId}/versions`;
			return medusaRequest("GET", path);
		},

		getMacroWithVersions(macroId) {
			const path = `/admin/order/macros/${macroId}/with-versions`;
			return medusaRequest("GET", path);
		},
	},
	macros: {
		getEssentialMacros(orderId) {
			let path = "/admin/macros/essentials";
			if (orderId) {
				path += `?order_id=${encodeURIComponent(orderId)}`;
			}
			return medusaRequest("GET", path);
		},

		getProductVariants(macroId) {
			const path = `/admin/macros/${macroId}/product-variants`;
			return medusaRequest("GET", path);
		},

		getOrderMacros(orderId, abortController = new AbortController()) {
			const path = `/admin/order/${orderId}/macros/recommended`;
			return medusaRequest("GET", path, abortController);
		},

		get(id) {
			const path = `/admin/macros/${id}`;
			return medusaRequest("GET", path);
		},

		getSteps(macroId) {
			const path = `/admin/macros/${macroId}/steps`;
			return medusaRequest("GET", path);
		},

		isV2(macroId) {
			const path = `/admin/macros/${macroId}/is-v2`;
			return medusaRequest("GET", path);
		},

		executeV2(orderId, macroId, payload) {
			const path = `/admin/order/${orderId}/macros/${macroId}/run-v2`;
			return medusaRequest("POST", path, payload);
		},

		previewV2(orderId, macroId, payload) {
			const path = `/admin/order/${orderId}/macros/${macroId}/preview-v2`;
			return medusaRequest("POST", path, payload);
		},

		create(payload) {
			const path = `/admin/macros`;
			return medusaRequest("POST", path, payload);
		},

		duplicate(macroId) {
			const path = `/admin/macros/${macroId}/duplicate`;
			return medusaRequest("POST", path);
		},

		update(id, payload) {
			const path = `/admin/macros/${id}`;
			return medusaRequest("PATCH", path, payload);
		},

		evaluateProductVariants(conditions) {
			return medusaRequest("POST", "/admin/macros/evaluate-product-variants", {
				conditions,
			});
		},
		updateConditions: (macroId, conditions) =>
			medusaRequest("POST", `/admin/macros/${macroId}/conditions`, {
				conditions,
			}),

		getMacroEdits(macroId, params = {}) {
			const query = new URLSearchParams(params).toString();
			const path = `/admin/macro/activity/macro/${macroId}${query ? `?${query}` : ""}`;
			return medusaRequest("GET", path);
		},

		getMacroActivity(macroId, params = {}) {
			const query = new URLSearchParams({ macroId, ...params }).toString();
			const path = `/admin/macro/activity${query ? `?${query}` : ""}`;
			return medusaRequest("GET", path);
		},

		getRecentActivity(params = {}) {
			const query = new URLSearchParams(params).toString();
			const path = `/admin/macro/activity${query ? `?${query}` : ""}`;
			return medusaRequest("GET", path);
		},

		getArchivedMacros(params = {}) {
			const queryParams = { ...params, status: "archived" };
			const query = new URLSearchParams(queryParams).toString();
			const path = `/admin/macros${query ? `?${query}` : ""}`;
			return medusaRequest("GET", path);
		},

		getArchivedMacro(archivedMacroId) {
			const path = `/admin/macro/archived/${archivedMacroId}`;
			return medusaRequest("GET", path);
		},

		archiveMacro(macroId, reason) {
			const path = `/admin/macros/${macroId}/archive`;
			return medusaRequest("POST", path, { reason });
		},

		restoreArchivedMacro(archivedMacroId) {
			const path = `/admin/macro/archived/${archivedMacroId}/restore`;
			return medusaRequest("POST", path);
		},

		getMacroVersions(macroId) {
			const path = `/admin/macros/${macroId}/versions`;
			return medusaRequest("GET", path);
		},

		updateMacroDetails(macroId, details) {
			const path = `/admin/macros/${macroId}/details`;
			return medusaRequest("PATCH", path, details);
		},

		getUserMacros() {
			const path = `/admin/order/macros`;
			return medusaRequest("GET", path);
		},

		getTemplateVariables(macroType) {
			const params = macroType ? `?macroType=${macroType}` : "";
			const path = `/admin/macros/template-variables${params}`;
			return medusaRequest("GET", path);
		},

		getSendgridTemplateHtml(templateId) {
			const path = `/admin/sendgrid/template/${templateId}/html`;
			return medusaRequest("GET", path);
		},
	},
	pharmacy: {
		list() {
			const path = `/admin/pharmacy`;
			return medusaRequest("GET", path);
		},
	},

	shippingOptions: {
		create(shippingOption) {
			const path = `/admin/shipping-options`;
			return medusaRequest("POST", path, shippingOption);
		},

		retrieve(id) {
			const path = `/admin/shipping-options/${id}`;
			return medusaRequest("GET", path);
		},

		delete(id) {
			const path = `/admin/shipping-options/${id}`;
			return medusaRequest("DELETE", path);
		},

		list(search = {}) {
			const params = Object.keys(search)
				.map((k) => `${k}=${search[k]}`)
				.join("&");
			const path = `/admin/shipping-options${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},

		update(id, update) {
			const path = `/admin/shipping-options/${id}`;
			return medusaRequest("POST", path, update);
		},
	},
	shipments: {
		cancel(fulfillmentId) {
			return medusaRequest("POST", `/admin/shipments/${fulfillmentId}/cancel`);
		},

		getHoldLocations(providerType, serviceType, recipient) {
			const path = `/admin/shipments/hold-locations`;
			return medusaRequest("POST", path, {
				providerType,
				serviceType,
				recipient,
			});
		},
	},
	manifests: {
		list(status) {
			const path = `/admin/manifests${status ? `?status=${status}` : ""}`;
			return medusaRequest("GET", path);
		},
		get(manifestId) {
			const path = `/admin/manifests/${manifestId}`;
			return medusaRequest("GET", path);
		},
		submit(manifestId) {
			const path = `/admin/manifests/${manifestId}/submit`;
			return medusaRequest("POST", path);
		},
	},

	discounts: {
		create(discount) {
			const path = `/admin/discounts`;
			return medusaRequest("POST", path, discount);
		},

		retrieve(discountId) {
			const path = `/admin/discounts/${discountId}`;
			return medusaRequest("GET", path);
		},

		update(discountId, update) {
			const path = `/admin/discounts/${discountId}`;
			return medusaRequest("POST", path, update);
		},

		delete(discountId) {
			const path = `/admin/discounts/${discountId}`;
			return medusaRequest("DELETE", path);
		},

		list(search = {}) {
			const params = buildQueryFromObject(search);
			const path = `/admin/discounts${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},

		listV2() {
			const path = `/admin/v2/discounts`;
			return medusaRequest("GET", path);
		},

		retrieveByCode(code) {
			const path = `/admin/discounts/code/${code}`;
			return medusaRequest("GET", path);
		},
		retrieveIngestCondition(discountId) {
			const path = `/admin/discounts/${discountId}/marketing-channel-ingest-condition`;
			return medusaRequest("GET", path);
		},
		createIngestCondition(discountId, channelId) {
			const path = `/admin/discounts/${discountId}/marketing-channel/${channelId}`;
			return medusaRequest("POST", path);
		},
		deleteIngestCondition(discountId, channelId) {
			const path = `/admin/discounts/${discountId}/marketing-channel/${channelId}`;
			return medusaRequest("DELETE", path);
		},
	},

	regions: {
		list() {
			const path = `/admin/regions`;
			return medusaRequest("GET", path);
		},

		retrieve(id) {
			const path = `/admin/regions/${id}`;
			return medusaRequest("GET", path);
		},

		create(region) {
			const path = `/admin/regions`;
			return medusaRequest("POST", path, region);
		},

		update(id, region) {
			const path = `/admin/regions/${id}`;
			return medusaRequest("POST", path, region);
		},

		delete(id) {
			const path = `/admin/regions/${id}`;
			return medusaRequest("DELETE", path);
		},

		fulfillmentOptions: {
			list(regionId) {
				const path = `/admin/regions/${regionId}/fulfillment-options`;
				return medusaRequest("GET", path);
			},
		},
	},

	uploads: {
		create(files) {
			const path = `/admin/uploads`;
			const formData = new FormData();
			for (const file of files) {
				formData.append("files", file);
			}
			return medusaRequest("POST", path, formData);
		},

		convertFile(sourceFormat, targetFormat, file) {
			const formData = new FormData();
			formData.append("file", file);
			formData.append("sourceFormat", sourceFormat);
			formData.append("targetFormat", targetFormat);

			const path = `/files/convert`;
			return medusaRequest("POST", path, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
		},
	},

	draftOrders: {
		create(draftOrder) {
			const path = `/admin/draft-orders`;
			return medusaRequest("POST", path, draftOrder);
		},

		addLineItem(draftOrderId, line) {
			const path = `/admin/draft-orders/${draftOrderId}/line-items`;
			return medusaRequest("POST", path, line);
		},

		updateLineItem(draftOrderId, lineId, line) {
			const path = `/admin/draft-orders/${draftOrderId}/line-items/${lineId}`;
			return medusaRequest("POST", path, line);
		},

		deleteLineItem(draftOrderId, lineId) {
			const path = `/admin/draft-orders/${draftOrderId}/line-items/${lineId}`;
			return medusaRequest("DELETE", path);
		},

		retrieve(id) {
			const path = `/admin/draft-orders/${id}`;
			return medusaRequest("GET", path);
		},

		delete(id) {
			const path = `/admin/draft-orders/${id}`;
			return medusaRequest("DELETE", path);
		},

		update(id, payload) {
			const path = `/admin/draft-orders/${id}`;
			return medusaRequest("POST", path, payload);
		},

		registerSystemPayment(id) {
			const path = `/admin/draft-orders/${id}/pay`;
			return medusaRequest("POST", path);
		},

		list(search = {}) {
			const params = Object.keys(search)
				.map((k) => {
					if (search[k] === "" || search[k] === null) {
						return null;
					}
					return `${k}=${search[k]}`;
				})
				.filter((s) => !!s)
				.join("&");
			const path = `/admin/draft-orders${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},
	},
	invites: {
		create(data) {
			const path = `/admin/invites`;
			return medusaRequest("POST", path, data);
		},
		createV2(data) {
			const path = `/admin/v2/invites`;
			return medusaRequest("POST", path, data);
		},
		resend(inviteId) {
			const path = `/admin/invites/${inviteId}/resend`;
			return medusaRequest("POST", path);
		},
		delete(inviteId) {
			const path = `/admin/invites/${inviteId}`;
			return medusaRequest("DELETE", path);
		},
		list() {
			const path = `/admin/invites`;
			return medusaRequest("GET", path);
		},
		accept(data) {
			const path = `/admin/invites/accept`;
			return medusaRequest("POST", path, data);
		},
		acceptV2(data) {
			const path = `/admin/v2/invites/accept`;
			return medusaRequest("POST", path, data);
		},
	},
	users: {
		resetPasswordToken(data) {
			const path = `/admin/users/password-token`;
			return medusaRequest("POST", path, data);
		},
		resetPassword(data) {
			const path = `/admin/users/reset-password`;
			return medusaRequest("POST", path, data);
		},

		list() {
			const path = `/admin/v2/users`;
			return medusaRequest("GET", path);
		},

		retrieve(userId) {
			const path = `/admin/users/${userId}`;
			return medusaRequest("GET", path);
		},

		update(userId, data) {
			const path = `/admin/users/${userId}`;
			return medusaRequest("POST", path, data);
		},

		updateV2(userId, data) {
			const path = `/admin/v2/users/${userId}`;
			return medusaRequest("PUT", path, data);
		},

		delete(userId) {
			const path = `/admin/users/${userId}`;
			return medusaRequest("DELETE", path);
		},

		getUserV2(userId) {
			const path = `/admin/v2/users/${userId}`;
			return medusaRequest("GET", path);
		},
		resetPasswordV2() {
			const path = `/admin/v2/users/me/reset-password-url`;
			return medusaRequest("GET", path);
		},
	},
	signatures: {
		getUrlFromId(templateId) {
			const path = `/files/templates/${templateId}/url`;
			return medusaRequest("GET", path);
		},
		getDocumentTypes() {
			const path = `/files/templates/types`;
			return medusaRequest("POST", path);
		},
		getSignedPrescriptions(orderId) {
			const path = `/admin/order/${orderId}/prescriptions`;
			return medusaRequest("GET", path);
		},
		getSignedDocumentByRequestId(orderId, requestId) {
			const path = `/admin/files/${orderId}/signatures/${requestId}/signed`;
			return medusaRequest("GET", path);
		},
		getAllSignedDocumentsForOrder(orderId) {
			const path = `/admin/files/${orderId}/signatures/signed`;
			return medusaRequest("GET", path);
		},
	},
	forms: {
		getForms(findOptions) {
			const path = `/admin/forms/`;
			return medusaRequest("GET", path, findOptions);
		},
		getFormsForOrder(orderId) {
			const path = `/admin/forms/order/${orderId}`;
			return medusaRequest("GET", path);
		},
	},
	marketingChannels: {
		list() {
			const path = `/admin/marketing-channel`;
			return medusaRequest("GET", path);
		},
	},
	clinician: {
		list(search = {}) {
			const params = Object.keys(search)
				.map((k) => {
					if (search[k] === "" || search[k] === null) {
						return null;
					}
					return `${k}=${search[k]}`;
				})
				.filter((s) => !!s)
				.join("&");
			const path = `/admin/clinician${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},

		create(data) {
			const path = `/admin/clinician`;
			return medusaRequest("POST", path, data);
		},

		retrieve(id) {
			const path = `/admin/clinician/${id}`;
			return medusaRequest("GET", path);
		},

		update(id, data) {
			const path = `/admin/clinician/${id}`;
			return medusaRequest("PUT", path, data);
		},

		delete(id) {
			const path = `/admin/clinician/${id}`;
			return medusaRequest("DELETE", path);
		},

		getLoad(id) {
			const path = `/admin/clinician/${id}/load`;
			return medusaRequest("GET", path);
		},

		uploadCertification(id, data) {
			const path = `/admin/clinician/${id}/certification`;
			return medusaRequest("POST", path, data);
		},

		uploadCertificationFile(id, formData) {
			const path = `/admin/clinician/${id}/certification/file`;
			return medusaRequest("POST", path, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
		},
		checkProvinceCpsc(orderId) {
			const path = `/admin/clinician/check-province-cpsc/${orderId}`;
			return medusaRequest("GET", path);
		},
		getAvailableForProvince(orderId) {
			const path = `/admin/clinician/available-for-province/${orderId}`;
			return medusaRequest("GET", path);
		},
	},
	trackingTags: {
		create(data) {
			const path = `/admin/tracking-tags`;
			return medusaRequest("POST", path, data);
		},

		retrieve(id) {
			const path = `/admin/tracking-tags/${id}`;
			return medusaRequest("GET", path);
		},

		list(search = {}) {
			const params = Object.keys(search)
				.map((k) => {
					if (search[k] === "" || search[k] === null) {
						return null;
					}
					return `${k}=${search[k]}`;
				})
				.filter((s) => !!s)
				.join("&");
			const path = `/admin/tracking-tags${params && `?${params}`}`;
			return medusaRequest("GET", path);
		},

		update(id, update) {
			const path = `/admin/tracking-tags/${id}`;
			return medusaRequest("PUT", path, update);
		},

		delete(id) {
			const path = `/admin/tracking-tags/${id}`;
			return medusaRequest("DELETE", path);
		},
	},
	permission: {
		// TODO Evan: consider making CRUD functions here, but tbh that might be overkill for what we want rn
		authenticate(entityName, action, entityId) {
			const path = `/admin/authz/entity-name/${entityName}/${entityId ? `entity-id/${entityId}/` : ""}action/${action}`;
			return medusaRequest("GET", path);
		},

		getPermissionsByRole(role) {
			const path = `/admin/permissions/role/${role}`;
			return medusaRequest("GET", path);
		},

		getPermissionsByUser() {
			const path = `/admin/permissions/user`;
			return medusaRequest("GET", path);
		},
	},
	encounterNotes: {
		getTemplates() {
			const path = `/admin/encounter-note-templates`;
			return medusaRequest("GET", path);
		},

		createTemplate(data) {
			const path = `/admin/encounter-note-templates`;
			return medusaRequest("POST", path, data);
		},

		updateTemplate(id, data) {
			const path = `/admin/encounter-note-templates/${id}`;
			return medusaRequest("PUT", path, data);
		},

		deleteTemplate(id) {
			const path = `/admin/encounter-note-templates/${id}`;
			return medusaRequest("DELETE", path);
		},

		create(data) {
			const path = `/admin/encounter-notes`;
			return medusaRequest("POST", path, data);
		},

		update(id, data) {
			const path = `/admin/encounter-notes/${id}`;
			return medusaRequest("PUT", path, data);
		},

		delete(id) {
			const path = `/admin/encounter-notes/${id}`;
			return medusaRequest("DELETE", path);
		},
	},

	releaseNotes: {
		getReleaseNoteById(id) {
			const path = `/admin/release-notes/${id}`;
			return medusaRequest("GET", path);
		},

		getAllReleaseNotes() {
			const path = `/admin/release-notes`;
			return medusaRequest("GET", path);
		},
		generateReleaseNotes() {
			const path = `/admin/release-notes/generate`;
			return medusaRequest("POST", path);
		},
		updateReleaseNote(id, data) {
			const path = `/admin/release-notes/${id}`;
			return medusaRequest("PUT", path, data);
		},
	},
	messageTemplates: {
		getByName(name) {
			const path = `/admin/message-templates/${name}`;
			return medusaRequest("GET", path);
		},
		update(id, data) {
			const path = `/admin/message-templates/${id}`;
			return medusaRequest("PUT", path, data);
		},
	},
};
